function getFocusableElements(container) {
  if (!container) return [];

  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

/**
 * Publishes a cross-sell funnel event to the Shopify customer-events pixel.
 *
 * Attribution runs on two tracks: the `properties[__cross_sell]` line item property is the
 * order-level revenue record (visible in admin), while these events measure the funnel -
 * the pixel sandbox cannot read line item properties off a standard add-to-cart event.
 *
 * @param {'cross_sell_impression'|'cross_sell_added'} name
 * @param {{placement: string, product_id?: string, variant_id?: string, value?: number, parent_product_id?: string}} params
 */
function publishCrossSellEvent(name, params) {
  if (typeof window.Shopify?.analytics?.publish !== 'function') return;

  try {
    window.Shopify.analytics.publish(name, {
      currency: window.Shopify?.currency?.active || 'AUD',
      ...params,
    });
  } catch (error) {
    console.warn('Unable to publish cross-sell event:', error);
  }
}
window.publishCrossSellEvent = publishCrossSellEvent;

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
    this.bindButtonEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  bindButtonEvents() {
    this.querySelector('.menu-drawer__general__close-button')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.onSummaryClick(e);
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.closest('details');
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)'))
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.scrollHint = this.querySelector('#scroll_hint');
    this.scrollView = this.scrollHint.parentElement;
    this.bindMoreEvents();
  }

  bindMoreEvents() {
    this.querySelector('#menu-drawer-close').addEventListener('click', this.onSummaryClick.bind(this));
    this.scrollHint.addEventListener('click', this.onScrollHintClick.bind(this));
    this.scrollView.addEventListener('scroll', debounce(this.onScroll.bind(this), 300).bind(this));
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
      this.style.setProperty('--menu-drawer-submenu-height', `${this.querySelector('.menu-drawer__utility-links')?.clientHeight + 1}px`);
      this.onScroll();
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    this.style.setProperty('--menu-drawer-submenu-height', `${this.querySelector('.menu-drawer__utility-links').clientHeight + 1}px`);
    this.onScroll();
  };


  onScroll() {
    if (this.scrollView.scrollTop + this.scrollView.clientHeight + 22.4 >= this.scrollView.scrollHeight) //22.4 is wrapper padding
      this.scrollHint.classList.add('hidden')
    else
      this.scrollHint.classList.remove('hidden')
  }

  onScrollHintClick() {
    this.scrollView.scrollBy(0, 50)
  }
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.querySelectorAll('.extra-close-button').forEach((button) => {
      button.addEventListener('click', this.hide.bind(this, false));
    });
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
    document.body.dispatchEvent(new CustomEvent('modalOpened', { detail: { modal: this } }));
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed', { detail: { modal: this } }));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    /* Custom Code to handle button clicks for deferred media */
    const galleryViewer = document.querySelector('[id^="GalleryViewer"]');
    if (galleryViewer) {
      galleryViewer.querySelectorAll('.deferred-media').forEach((deferredMedia) => {
        const video = deferredMedia?.querySelector('video');
        if (video) {
          video.pause();
        }
      });
    }
    /* End Custom Code to handle button clicks for deferred media */
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadStyleOnce(href) {
    if (!href) return Promise.resolve(null);
    if (window.luxoLoadStyleOnce) return window.luxoLoadStyleOnce(href);

    const normalizedHref = new URL(href, document.baseURI).href;
    const existingLink = Array.from(document.querySelectorAll('link[href]')).find(
      (link) => link.href === normalizedHref || link.getAttribute('href') === href
    );

    if (existingLink?.sheet || existingLink?.dataset.loaded === 'true') return Promise.resolve(existingLink);

    return new Promise((resolve) => {
      const link = existingLink || document.createElement('link');
      const markReady = () => {
        link.dataset.loaded = 'true';
        resolve(link);
      };

      link.addEventListener('load', markReady, { once: true });
      link.addEventListener('error', markReady, { once: true });
      link.rel = 'stylesheet';

      if (!existingLink) {
        link.href = href;
        document.head.appendChild(link);
      }
    });
  }

  loadScriptOnce(src, attrs = {}) {
    if (!src) return Promise.resolve(null);
    if (window.luxoLoadScriptOnce) return window.luxoLoadScriptOnce(src, attrs);

    const normalizedSrc = new URL(src, document.baseURI).href;
    const existingScript = Array.from(document.scripts).find(
      (script) => script.src === normalizedSrc || script.getAttribute('src') === src
    );

    if (existingScript?.dataset.loaded === 'true') return Promise.resolve(existingScript);

    return new Promise((resolve, reject) => {
      const script = existingScript || document.createElement('script');
      const markReady = () => {
        script.dataset.loaded = 'true';
        resolve(script);
      };
      const markError = () => reject(new Error(`Failed to load script: ${src}`));

      script.addEventListener('load', markReady, { once: true });
      script.addEventListener('error', markError, { once: true });

      if (!existingScript) {
        Object.entries(attrs).forEach(([key, value]) => {
          if (value === true) script.setAttribute(key, key);
          else if (value !== false && value != null) script.setAttribute(key, value);
        });
        script.src = src;
        document.body.appendChild(script);
      }
    });
  }

  loadDeferredAssets(hasQuickAdd) {
    const assetPromises = [];

    if (this.dataset.stylesheetUrl) assetPromises.push(this.loadStyleOnce(this.dataset.stylesheetUrl));
    if (hasQuickAdd && this.dataset.quickAddStylesheetUrl) {
      assetPromises.push(this.loadStyleOnce(this.dataset.quickAddStylesheetUrl));
    }
    if (hasQuickAdd && this.dataset.quickAddScriptUrl) {
      assetPromises.push(this.loadScriptOnce(this.dataset.quickAddScriptUrl, { defer: true }));
    }

    return Promise.all(assetPromises);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');
        const hasRecommendations = recommendations?.innerHTML.trim().length;
        const hasQuickAdd = !!recommendations?.querySelector('.quick-add');
        const hasCards = !!html.querySelector('.grid__item');

        if (!hasRecommendations) {
          if (this.classList.contains('complementary-products')) this.remove();
          return;
        }

        this.loadDeferredAssets(hasQuickAdd).catch((e) => console.error(e)).finally(() => {
          this.innerHTML = recommendations.innerHTML;

          if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
          }

          if (hasCards) {
            this.classList.add('product-recommendations--loaded', 'scroll-trigger', 'animate--slide-in');
          }
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class ApiService {
  /**
   * @param {object} config 
   * @param {string} [config.base_url]  base url of endpoint
   * @param {string} [config.access_token] bearer token
   * @param {string} [config.content_type] content type of the data/payload
   * @param {boolean} [config.no_cors] bearer token
   * @param {object[]} [config.headers] array of headers
   */
  constructor(config) {
    this.config = config;
  }

  /**
   * 
   * @param {string} method HTTP request method
   * @param {string} servicePath endpoint without the base url
   * @param {any} body if expected payload content is json, JSON.stringify the body first
   * @returns  {Promise<{success:boolean,data:any}>}
   */
  async runService(method, servicePath, body) {
    const config = this.config
    const fetchOptions = {
      method,
      // headers: {
      //   'Content-Type': 'application/json',    //removed to allow sending of form data or query string; JSON.stringify the body if server expects JSON
      // }
    };
    if (config && config.no_cors)
      fetchOptions.mode = "no-cors";

    if (config && config.access_token)
      fetchOptions.headers.Authorization = `Bearer ${config.access_token}`;

    if (method.toUpperCase() === 'POST')
      fetchOptions.body = body;

    if (config && config.headers && config.headers.length > 0) {
      fetchOptions.headers = {};
      config.headers.forEach(header => {
        fetchOptions.headers[header.name] = header.value;
      });
    }

    const response = await fetch(config.base_url + servicePath, fetchOptions);
    if (response.status === 200) {
      const resText = await response.text();
      return {
        success: true,
        data: resText ? JSON.parse(resText) : []
      };
    }

    return {
      success: false,
      data: null,
    };
  }

  get(servicePath, body) {
    return this.runService('GET', servicePath, body);
  }

  post(servicePath, body) {
    return this.runService('POST', servicePath, body);
  }
}

/**
 * use api.[module].createService method and instantiate an api service to avoid overwriting configs which might result to incorrect requests
 */
const api = {
  mergeConfigs(defaultConfig, instanceConfig) { //use default config's object structure but replace with instance's prop-value if exists, otherwise use default value
    const merge = (a, b) => Object.fromEntries(
      Object.entries(a).map(([k, v]) => [
        k,
        b && Object.prototype.hasOwnProperty.call(b, k)
          ? (v && typeof v === 'object' && !Array.isArray(v) && typeof b[k] === 'object'
            ? merge(v, b[k])
            : b[k]
          )
          : v
      ])
    );

    const merged = merge(defaultConfig, instanceConfig);
    return merged;
  },
  luxo: {
    default_config: {
      base_url: "https://api.luxoliving.com.au/",
      access_token: "",
      no_cors: false,
      headers: []
    },
    llaravel_config: {
      base_url: 'https://llapi.luxoliving.com.au',
      headers: [
        { name: 'Content-Type', value: 'application/json' }
      ]
    },
    /**
     * @param {object} config 
     * @param {string} [config.base_url]  base url of endpoint
     * @param {string} [config.access_token] bearer token
     * @param {boolean} [config.no_cors] bearer token
     * 
     * @returns {ApiService}
     */
    createService(config = {}) {
      return new ApiService(api.mergeConfigs(this.default_config, config));
    }
  }
}

function getDistance(latitude1, longitude1, latitude2, longitude2) {
  let earth_radius = 6371;

  let dLat = degreesToRadians(latitude2 - latitude1);
  let dLon = degreesToRadians(longitude2 - longitude1);

  let a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(degreesToRadians(latitude1)) * Math.cos(degreesToRadians(latitude2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  let c = 2 * Math.asin(Math.sqrt(a));
  let d = earth_radius * c;

  return d;
}

function degreesToRadians(degrees) {
  let pi = Math.PI;
  return degrees * (pi / 180);
}

const userLocation = {
  init() {
    if (!this.getStoredLocation() || !this.getUserSetLocation()) {
      // User Location Not Set Yet
      // If user location not saved, check if region_id in url
      const urlParams = new URLSearchParams(window.location.search);
      const regionId = urlParams.get('region_id');
      let regionLocation = null;
      if (regionId) {
        regionLocation = REGION_LOCATIONS.find(region => region.id == regionId);
      }

      if (regionLocation && !this.getUserSetLocation()) {
        this.setStoredLocation(regionLocation.suburb, regionLocation.state, regionLocation.postcode);
      } else {
        const getUserSetLocation = this.getUserSetLocation();
        const getGeolocationSet = this.getGeolocationSet();
        if (!getUserSetLocation && !getGeolocationSet) {
          this.setStoredLocation('SYDNEY', 'NSW', '2000');
          if (!this.getGeoCoordinates()) {
            // If no geo coordinates found, get geolocation
            this.getGeolocation();
          }
        }
      }
    }
  },
  getUserSetLocation() {
    const userSetLocation = localStorage.getItem(USER_LOCATION_STORAGE.userSetLocation);
    if (userSetLocation == 'true') {
      return true;
    }
    return false;
  },
  isUserLocationSaved() {
    return ((localStorage.getItem(USER_LOCATION_STORAGE.suburb) !== null) && (localStorage.getItem(USER_LOCATION_STORAGE.state) !== null) && (localStorage.getItem(USER_LOCATION_STORAGE.postcode) !== null));
  },
  getStoredLocation() {
    if (this.isUserLocationSaved()) {
      return {
        suburb: localStorage.getItem(USER_LOCATION_STORAGE.suburb),
        state: localStorage.getItem(USER_LOCATION_STORAGE.state),
        postcode: localStorage.getItem(USER_LOCATION_STORAGE.postcode)
      }
    }

    return false;
  },
  saveRegionLocation() {
    if (regionId == '000001') {
      this.setStoredLocation('SYDNEY', 'NSW', '2000');
    } else if (regionId == '000002') {
      this.setStoredLocation('MELBOURNE', 'VIC', '3000');
    } else if (regionId == '000003') {
      this.setStoredLocation('BRISBANE', 'QLD', '4000');
    } else if (regionId == '000004') {
      this.setStoredLocation('ADELAIDE', 'SA', '5000');
    } else if (regionId == '000005') {
      this.setStoredLocation('CANBERRA', 'ACT', '2600');
    } else if (regionId == '000006') {
      this.setStoredLocation('DARWIN CITY', 'NT', '800');
    } else if (regionId == '000088') {
      // DLX QLD
      this.setStoredLocation('BRISBANE', 'QLD', '4000');
    }
  },
  getGeolocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.getPosition(position);
      }, (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            console.log("User denied the request for Geolocation.");
            break;
          case error.POSITION_UNAVAILABLE:
            console.log("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            console.log("The request to get user location timed out.");
            break;
          case error.UNKNOWN_ERROR:
            console.log("An unknown error occurred.");
            break;
        }
      });
    }
  },
  getPosition(position) {
    this.setGeoCoordinates(position.coords.latitude, position.coords.longitude);
    this.setGeolocationSet();

    CITY_CENTRES.forEach((city) => {
      const distance = getDistance(position.coords.latitude, position.coords.longitude, city.latitude, city.longitude);
      if (distance <= city.radius) {
        this.setStoredLocation(city.suburb, city.state, city.postcode);
        localStorage.setItem(USER_LOCATION_STORAGE.geolocationCode, city.code);
      }
    });
  },
  isInGeolocationCity(latitude, longitude, city) {
    let isInArea = false;
    const distance = getDistance(latitude, longitude, city.latitude, city.longitude);
    if (distance <= city.radius) {
      isInArea = true;
    }
    return isInArea;
  },
  setStoredLocation(suburb, state, postcode) {
    localStorage.setItem(USER_LOCATION_STORAGE.suburb, suburb);
    localStorage.setItem(USER_LOCATION_STORAGE.state, state);
    localStorage.setItem(USER_LOCATION_STORAGE.postcode, postcode);
    publish(PUB_SUB_EVENTS.userLocationSet, {
      suburb: suburb,
      state: state,
      postcode: postcode
    });
  },
  setGeoCoordinates(latitude, longitude) {
    localStorage.setItem(USER_LOCATION_STORAGE.latitude, latitude);
    localStorage.setItem(USER_LOCATION_STORAGE.longitude, longitude);
  },
  getGeoCoordinates() {
    const latitude = localStorage.getItem(USER_LOCATION_STORAGE.latitude);
    const longitude = localStorage.getItem(USER_LOCATION_STORAGE.longitude);

    if (!latitude || !longitude) {
      return false;
    }

    return {
      latitude,
      longitude
    };
  },
  getGeolocationSet() {
    return localStorage.getItem(USER_LOCATION_STORAGE.userSetGeolocation) == 'true';
  },
  setGeolocationSet() {
    localStorage.setItem(USER_LOCATION_STORAGE.userSetGeolocation, true);
  },
  getLocationSelected() {
    return localStorage.getItem(USER_LOCATION_STORAGE.userSetLocation);
  },
  setLocationSelected() {
    localStorage.setItem(USER_LOCATION_STORAGE.userSetLocation, true);
  }
}

userLocation.init();

function capitalizeWords(text) {
  return text.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
}
const getTimeDifference = (date1, date2) => {
  return Math.abs(date2 - date1);
}
const getDayDifference = (date1, date2) => {
  return Math.ceil(getTimeDifference(date1, date2) / (1000 * 60 * 60 * 24));
}
const getMonthLong = (date, option = "long") => {
  return (new Date(date)).toLocaleString(undefined, { month: option });
}
const getDayLong = (date, option = "long") => {
  const currentSydneyDate = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Sydney" }));
  const dayDiff = getDayDifference(date, currentSydneyDate)
  switch (dayDiff) {
    case 1:
      if (currentSydneyDate.getDate() == date.getDate())
        return "today"
    case 2:
      if (Math.abs(date.getDate() - currentSydneyDate.getDate()) < 2)
        return "tomorrow"
    default:
      return date.toLocaleString(undefined, { weekday: option })
  }
}
const getOrdinalDate = (date) => {
  if (!(date instanceof Date)) {
    throw new Error('Input must be a Date object');
  }

  const day = date.getDate();

  const suffix = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  return `${day}${suffix(day)}`;
}

// Returns the promise date's year (as a string) only when it falls outside the
// current Sydney calendar year, so cross-year delivery estimates are clear.
// Uses the underlying fulfilment date rather than inferring from the weekday.
const sydneyYearFormatter = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', year: 'numeric' });
const getPromiseDateYearSuffix = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return '';
  const year = date.getFullYear();
  return year !== Number(sydneyYearFormatter.format(new Date())) ? ` ${year}` : '';
}

class Collapsible extends HTMLElement { //same fxn with accordion blocks

  activeClass = 'open'
  selector = '[data-collapsible]'

  constructor() {
    super();
    this.triggerEl = this.querySelector(this.selector);
    this.targetEl = this.querySelector(`#${this.triggerEl.dataset.collapsible}`);
    this.targetEl.classList.add('collapsible');
    // this.targetEl.setAttribute('hidden', true);
    this.bindEvents();
  }

  bindEvents() {
    this.triggerEl.addEventListener('click', this.toggle.bind(this))
  }

  expand() {
    this.targetEl.classList.add(this.activeClass);
    this.triggerEl.classList.add(this.activeClass);
    this.triggerEl.setAttribute('aria-expanded', true);
    this.triggerEl.setAttribute('aria-hidden', false);
    this.targetEl.setAttribute('aria-hidden', false);
    // this.targetEl.removeAttribute('hidden');
  }

  collapse() {
    this.targetEl.classList.remove(this.activeClass);
    this.triggerEl.classList.remove(this.activeClass);
    this.triggerEl.setAttribute('aria-expanded', false);
    this.triggerEl.setAttribute('aria-hidden', false);
    this.targetEl.setAttribute('aria-hidden', true);
    // this.targetEl.setAttribute('hidden', true);
  }

  toggle() {
    if (this.targetEl.classList.contains(this.activeClass))
      this.collapse();
    else
      this.expand();
  }
}

customElements.define('collapsible-block', Collapsible);

/**
 * @class PromiseDateProductCards
 * @description Class to handle fetching and attaching promise dates to product cards
 * @method getSavedProduct
 * @method clearSavedProductPromiseDates
 * @method clearProductsInQueue
 * @method isProductSaved
 * @method isProductInQueue
 * @method putProductInQueue
 * @method attachPromiseDateToProductCard
 * @method fetchPromiseDates
 */
class PromiseDateProductCards {
  constructor() {
    this.savedProductPromiseDates = [];
    this.productsInQueue = [];
    this.laravelApiWebServer = api.luxo.createService({
      base_url: 'https://lapi.luxoliving.com.au',
      headers: [
        {
          name: 'Content-Type',
          value: 'application/json'
        }
      ]
    });
    // Set interval to fetch promise dates every 5 seconds
    setInterval(() => {
      if (this.productsInQueue.length > 0) {
        this.fetchPromiseDates();
      }
    }, 3000);
  }

  getSavedProduct(product) {
    let productSku = '';
    if (product.variants && product.variants[0].sku) {
      productSku = product.variants[0].sku;
    } else {
      if (product.sku) {
        productSku = product.sku;
      } else {
        return false;
      }
    }
    return this.savedProductPromiseDates.find(productInQueue => productInQueue.sku === productSku);
  }

  clearSavedProductPromiseDates() {
    this.savedProductPromiseDates = [];
  }

  clearProductsInQueue() {
    this.productsInQueue = [];
  }

  isProductSaved(product) {
    let productSku = '';
    if (product.variants && product.variants[0].sku) {
      productSku = product.variants[0].sku;
    } else {
      if (product.sku) {
        productSku = product.sku;
      } else {
        return false;
      }
    }
    return this.savedProductPromiseDates.find(productInQueue => productInQueue.sku === productSku) ? true : false;
  }

  isProductInQueue(product) {
    // const productSku = product.variants[0].sku;
    let productSku = '';
    if (product.variants && product.variants[0].sku) {
      productSku = product.variants[0].sku;
    } else {
      if (product.sku) {
        productSku = product.sku;
      } else {
        return false;
      }
    }
    return this.productsInQueue.find(productInQueue => productInQueue.sku === productSku);
  }

  putProductInQueue(product) {
    if (product.variants && product.variants[0].sku) {
      product.sku = product.variants[0].sku;
    }
    this.productsInQueue.push(product);
  }

  attachPromiseDateToProductCard(product) {
    const productCard = document.querySelector(`product-card[data-product-id="${product.id}"]`);
    if (!productCard) return;

    const productCards = document.querySelectorAll(`product-card[data-product-id="${product.id}"]`);
    productCards.forEach(productCard => {
      const productCardPromiseDate = productCard.querySelector('.card__promise-date');
      if (!productCardPromiseDate) return;
      productCardPromiseDate.innerHTML = '';
      const eta = product.promise_date;
      if (eta) {
        // productCardPromiseDate.textContent = promiseDate;
        if (eta.split('-').length != 3)
          return;

        // Valid Promise Date
        const promiseDate = new Date(eta);
        const formattedDay = getDayLong(promiseDate);
        const formattedMonth = getMonthLong(promiseDate);
        // const formattedOrdinalDate = getOrdinalDate(promiseDate);
        const formattedOrdinalDate = promiseDate.getDate();
        const formattedYear = getPromiseDateYearSuffix(promiseDate);
        productCardPromiseDate.innerHTML = `<span class="promise-date">Get it by <span class="highlight-date">${formattedDay} ${formattedOrdinalDate} ${formattedMonth}${formattedYear}</span></span>`;
      }
    });
  }

  async fetchPromiseDates() {
    const location = userLocation.getStoredLocation();
    const destination = {
      country_iso2: 'AU',
      state_iso2: location.state,
      city: location.suburb,
      zip: location.postcode
    };
    let items = this.productsInQueue.map(product => {
      let sku = '';
      if (product.variants && product.variants[0].sku) {
        sku = product.variants[0].sku;
      } else {
        if (product.sku) {
          sku = product.sku;
        } else {
          return {}
        }
      }
      // Check if bundle product
      let bundledProducts = [];
      if (product.metafields && product.metafields.bundled_products && product.metafields.bundled_products.length > 0) {
        product.metafields.bundled_products.forEach(bundledProduct => {
          bundledProducts.push({
            sku: bundledProduct.sku,
            quantity: 1
          });
        });
      }
      let requestProduct = {
        sku: sku,
        quantity: 1
      }
      if (bundledProducts.length > 0) {
        requestProduct.bundle = bundledProducts;
      }
      return requestProduct;
    });
    items = items.filter(item => Object.keys(item).length > 0);
    const request = {
      base_options: {
        destination: destination,
        items: items
      }
    };

    const tempProductsHold = this.productsInQueue;
    this.clearProductsInQueue();
    if (items.length == 0) return;

    // Fetch Promise Dates for Products
    const promiseDates = await this.laravelApiWebServer.post('/api/promisedDate/date', JSON.stringify(request)).then((response) => {
      if (response.success) {
        const productPromiseDates = response.data;
        if (productPromiseDates && productPromiseDates.length > 0) {
          productPromiseDates.forEach(productPromiseDate => {
            const promiseDate = productPromiseDate.eta;
            const product = tempProductsHold.find(productInQueue => productInQueue.sku === productPromiseDate.sku);
            if (product) {
              product.promise_date = promiseDate;
              this.savedProductPromiseDates.push(product);
              this.attachPromiseDateToProductCard(product);
            }
          });
        }
      } else {
        console.error('Error fetching promise dates', response);
      }
    }).catch((error) => {
      console.error('Error fetching promise dates', error);
    });
  }
}

const promiseDateProductCards = new PromiseDateProductCards();

function waitForElement(selector, callback, interval = 200, timeout = 5000) {
  const startTime = Date.now();

  const checkExist = setInterval(() => {
    const el = document.querySelector(selector);
    if (el) {
      clearInterval(checkExist);
      callback(el);
    } else if (Date.now() - startTime > timeout) {
      clearInterval(checkExist);
      console.warn(`Timeout waiting for ${selector}`);
    }
  }, interval);
}

waitForElement('.y-badge.yotpo-display-wrapper', (el) => {
  const numOfReviewsEl = el.querySelector('.y-badge-reviews');
  const certifiedReviewsEl = el.querySelector('.y-badges-certified');
  const numOfReviews = numOfReviewsEl.textContent.trim();
  const certifiedReviews = certifiedReviewsEl.textContent.trim();
  if (numOfReviews) {
    certifiedReviewsEl.innerHTML = `<strong>${numOfReviews}</strong> ${certifiedReviews}`;
  }
});

async function checkUserAlreadySubscribed(email) {
  const apiWebServer = api.luxo.createService({
    base_url: 'https://app.luxoliving.com.au',
    headers: [
      {
        name: 'Content-Type',
        value: 'application/json' // also fix MIME type typo
      }
    ]
  });

  const data = { email };

  try {
    const response = await apiWebServer.post('/shopify/klaviyo/checkUserSubscribed.php', JSON.stringify(data));
    console.log('Response from checkUserSubscribed:', response);
    if (response.success) {
      return response.data;
    } else {
      console.error('API error:', response);
      return null;
    }
  } catch (error) {
    console.error('Network error:', error);
    return null;
  }
}

waitForElement('[data-testid="klaviyo-form-VVwXcr"]', (form) => {
  if (form) {
    const feedbackContainer = document.createElement('div');
    feedbackContainer.className = 'feedback-container';

    // Append it to the first child of the form
    const thirdChild = form.children[0].children[2];
    if (thirdChild) {
      thirdChild.appendChild(feedbackContainer);
    }

    const inputEmail = form.querySelector('input[name="email"]');
    const submitButton = form.querySelector('button');
    const clonedSubmitButton = submitButton.cloneNode(true);

    clonedSubmitButton.classList.add('validate-email');
    clonedSubmitButton.setAttribute('type', 'submit');
    submitButton.style.display = 'none';
    submitButton.after(clonedSubmitButton);

    // Email submission logic
    function handleEmailSubmit(e) {
      e.preventDefault();
      e.stopPropagation();

      const loadingSpinnerHTML = `<div class="loading__spinner">
        <svg
          aria-hidden="true"
          focusable="false"
          class="spinner"
          viewBox="0 0 66 66"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>`;
      clonedSubmitButton.insertAdjacentHTML('afterend', loadingSpinnerHTML);
      const loadingSpinner = clonedSubmitButton.nextElementSibling;

      const email = inputEmail.value.trim();

      // Add your validation logic or API call here
      // e.g. checkUserAlreadySubscribed(email).then(...)
      checkUserAlreadySubscribed(email).then(response => {
        if (!response || !response.status) {
          console.error('Invalid or missing response from checkUserAlreadySubscribed:', response);
          return;
        }

        // Remove any previous message
        const oldMessage = form.querySelector('.klaviyo-form-success-message');
        if (oldMessage) oldMessage.remove();

        const feedbackContainer = form.querySelector('.feedback-container');

        if (response.status === 'failed') {
          const messageElement = document.createElement('div');
          messageElement.textContent = response.message || 'Subscription check failed.';
          messageElement.classList.add('klaviyo-form-success-message');
          feedbackContainer.appendChild(messageElement);
          loadingSpinner.remove(); // Remove loading spinner
        } else if (response.status === 'success') {
          console.log('User not yet subscribed — triggering original submit');
          loadingSpinner.remove(); // Remove loading spinner
          submitButton.click(); // native click
        }
      });
    }

    // Enter key listener
    inputEmail.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        handleEmailSubmit(e);
      }
    });

    // Click + touch listener
    ['click', 'touchstart'].forEach(eventType => {
      clonedSubmitButton.addEventListener(eventType, handleEmailSubmit);
    });
  }

});

/**
 * 
 * @param {Date} start start date
 * @param {Date} end end date
 * @param {number} maxDays max days per block
 * @returns {{start:Date,end:Date}[]}
 */
const getDateBlocks = (start, end, maxDays) => {
  let result = [];
  let e = new Date(end);
  while (e > start) {
    let s = new Date(e.getFullYear(), e.getMonth(), e.getDate() - maxDays);
    result.push({ start: s >= start ? s : new Date(start), end: new Date(e.setHours(23, 59, 59)) });
    e.setDate(e.getDate() - maxDays - 1);
  }
  return result;
}

class ShopifyHelper {
  getProductByHandle(handle) {
    const query = `
      query ProductByHandle($handle: String!) {
        product(handle: $handle) {
          id
          handle
          metafields(identifiers: [{ namespace: "custom", key: "bundled_products" }]) {
            namespace
            key
            type
            # ← this resolves the list of referenced products
            references(first: 50) {
              nodes {
                ... on Product {
                  id
                  handle
                  title
                  # get all variant SKUs (or trim to first available if you prefer)
                  variants(first: 50) {
                    nodes {
                      id
                      sku
                      availableForSale
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
    const variables = { handle };
    return this.graphQLFetch({ query, variables }).then(data => data ? data.product : null);
  }

  async graphQLFetch(body) {
    const res = await fetch('/api/2024-10/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': 'c964e17af188552c1eb57d03fbd8533e'
      },
      body: JSON.stringify(body),
      credentials: 'omit'
    });
    const { data, errors } = await res.json();
    if (errors) console.error(errors);
    return data;
  }
}

const shopifyHelper = new ShopifyHelper();

document.querySelector('#user-icon')?.addEventListener('click', (event) => {
  const klaviyoHub = document.querySelector('#k-hub')
  if (!klaviyoHub)
    return;
  event.preventDefault();
  event.stopPropagation();
  window.location.hash = '#k-hub';
  event.currentTarget.setAttribute('href', '#k-hub')
});

// Google Rating Badge - Click Event Tracking
// Called from renderBadge (snippets/third-party-scripts.liquid) once the badge iframe exists,
// since the badge script is deferred well past any fixed polling window.
window.luxoTrackRatingBadge = (el) => {
  // Check if desktop view
  if (window.innerWidth > 800) {
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight;

      // Stop observing once height exceeds 500px, this means user clicked to open the store rating
      if (h > 500) {
        if (typeof window.luxoPublishEvent === 'function') {
          window.luxoPublishEvent('opened_ratings_popup', {
            source: 'google_rating_badge'
          });
        }
        ro.disconnect();
      }
    });

    ro.observe(el);
  }
};
